import styled from "@emotion/styled";
import { useBlocNext } from "@blac/react";
import type { FC } from "react";
import { useCallback, useId, useMemo } from "react";
import MessageFiles from "src/ui/components/MessageFiles/MessageFiles";
import { useNavigate } from "react-router-dom";
import { Button, messageContentStyles } from "@9amhealth/shared";
import HtmlParser from "src/lib/HtmlParser";
import NotificationsCubit from "src/state/NotificationsCubit/NotificationsCubit";
import type { NotificationDisplayItem } from "src/state/NotificationsCubit/NotificationsCubit";
import { formatRelativeTime } from "src/lib/formatRelativeTime";
import translate from "src/lib/translate";
import { AppQueryPopupsController } from "src/ui/components/AppQueryPopups/AppQueryPopupsBloc";
import nineLogo from "src/ui/assets/logos/9am_chat_logo.svg";
import { chatConversationPath } from "src/ui/components/LeapChat/leapChatPaths";

const Card = styled.div`
  position: relative;
  display: flex;
  gap: 0.75rem;
  width: 100%;
  padding: 0.85rem 1rem;
  background: #fff;
  border: none;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  text-align: left;

  &[data-unread="true"] {
    background:
      linear-gradient(
        0deg,
        rgba(171, 212, 255, 0.32) 0%,
        rgba(171, 212, 255, 0.32) 100%
      ),
      #fff;
  }

  &:focus-visible {
    outline: 2px solid var(--Accessible-Blue, #3973da);
    outline-offset: 2px;
  }
`;

const LogoWrap = styled.div`
  flex-shrink: 0;
  width: 40px;
  height: 40px;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }
`;

const UnreadDot = styled.span`
  position: absolute;
  top: 0.85rem;
  left: calc(1rem + 34px);
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--color-highlight-blue, #80aeff);
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PreviewText = styled.div`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
  color: var(--Greys-Charcoal, #212121);
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;

const ExpandedContent = styled.div`
  ${messageContentStyles}
  margin: 0;
  color: var(--Greys-Charcoal, #212121);
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.25rem;

  .ask-sophie-btn {
    --btn-height: auto;
    --btn-padding-left: 0;
    --btn-padding-right: 0;
    --btn-padding-top: 0;
    --btn-padding-bottom: 0;
    min-width: 0;
    color: var(--Accessible-Blue, #3973da);
    font-size: 0.85rem;

    &:hover,
    &:active {
      box-shadow: none;
      opacity: 1;
      filter: none;
      transform: none;
    }
  }
`;

const TimeText = styled.span`
  font-size: 0.75rem;
  color: var(--color-charcoal-light, #999);
`;

const DateGroupLabel = styled.h3`
  margin: 0;
  padding: 0.5rem 0 0.25rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-charcoal-light, #999);
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const AskSophieIcon: FC = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M2.25 6.75H12.375C14.239 6.75 15.75 8.26104 15.75 10.125C15.75 11.989 14.239 13.5 12.375 13.5H9M2.25 6.75L5.25 3.75M2.25 6.75L5.25 9.75"
      stroke="#3973DA"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export { DateGroupLabel, TimeText, ExpandedContent, PreviewText };

export const NotificationItemCard: FC<{
  item: NotificationDisplayItem;
  expanded: boolean;
  onToggle: () => void;
}> = ({ item, expanded, onToggle }) => {
  const [, cubit] = useBlocNext(NotificationsCubit);
  const navigate = useNavigate();
  const contentId = useId();

  const hasFiles = item.files.length > 0;

  const parsedContent = useMemo(() => {
    if (!item.isHtml) {
      return null;
    }
    return new HtmlParser(item.rawContent, {
      convertMarkdownToHtml: true
    }).toJsx();
  }, [item.isHtml, item.rawContent]);

  const parsedPreview = useMemo(() => {
    if (!item.isHtml) {
      return null;
    }
    return new HtmlParser(item.previewContent, {
      convertMarkdownToHtml: true
    }).toJsx();
  }, [item.isHtml, item.previewContent]);

  const handleClick = useCallback(() => {
    if (expanded) {
      return;
    }
    if (item.isUnread) {
      void cubit.markAsRead(item.id);
    }
    onToggle();
  }, [expanded, item.isUnread, item.id, cubit, onToggle]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleClick();
      }
    },
    [handleClick]
  );

  const handleAskSophie = useCallback(() => {
    void cubit.startConversation(item.id).then((conversationId) => {
      if (conversationId) {
        AppQueryPopupsController.closePopup();
        navigate(chatConversationPath(conversationId));
      }
    });
  }, [cubit, item.id, navigate]);

  return (
    <Card
      role="button"
      tabIndex={0}
      data-unread={item.isUnread}
      aria-expanded={expanded}
      aria-controls={contentId}
      aria-label={
        item.isUnread
          ? translate("notifications.card.ariaLabelUnread", {
              preview: item.previewContent
            })
          : translate("notifications.card.ariaLabel", {
              preview: item.previewContent
            })
      }
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <LogoWrap>
        <img src={nineLogo} alt="" />
      </LogoWrap>
      {item.isUnread && !expanded && <UnreadDot aria-hidden="true" />}
      <Body id={contentId}>
        {expanded ? (
          <>
            <ExpandedContent>
              {parsedContent ?? item.rawContent}
            </ExpandedContent>
            {hasFiles && (
              <MessageFiles
                files={item.files}
                variant="card"
                downloadSource="notification"
              />
            )}
            <Footer>
              <TimeText>{formatRelativeTime(item.addedAt)}</TimeText>
              <Button
                theme="transparent"
                size="compact"
                hideArrow
                icon={<AskSophieIcon />}
                className="ask-sophie-btn"
                onPress={handleAskSophie}
                aria-label={translate("notifications.card.askSophieAria")}
              >
                {translate("notifications.card.askSophie")}
              </Button>
            </Footer>
          </>
        ) : (
          <>
            <PreviewText>{parsedPreview ?? item.previewContent}</PreviewText>
            <TimeText>{formatRelativeTime(item.addedAt)}</TimeText>
          </>
        )}
      </Body>
    </Card>
  );
};
